import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import React, { useState, useCallback, useEffect } from 'react';
import EditorSettings from '@wix/da-ds/pkg/Icons/24x24/EditorSettings';
import cn from 'classnames';
import SidePanel from '../../../../../components/menus/SidePanel/SidePanel';
import { useForm, useController } from 'react-hook-form';
import { getCurrentUser, getExtensionOptions, getExternalRenderer, getStoredCallback } from '../../../../../components/utils/tiptap';
import BooleanInput from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import Flex from '@wix/da-ds/pkg/Flex';
import { useTranslation } from 'react-i18next';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import ModalHeader from '@wix/da-ds/pkg/modals/dsPresets/Modal/ModalHeader';
import NumericField from '../../../../../components/lego/NumericField';
import { addMissingProtocol, validateHref } from '../../../../../components/utils/validateHref';
import { EXTENSION_TYPE_NAME } from '../../../constants';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import EditorIconButton from '../../../../../components/menus/_partials/EditorIconButton';
import buttonStyles from '../../../../../components/menus/buttons/buttons.scss';
import s from './Settings.scss';
const Settings = ({ editor })=>{
    var _attrs_deviation_author, _attrs_deviation;
    const { t } = useTranslation('editor');
    const attrs = editor.getAttributes(EXTENSION_TYPE_NAME);
    const options = getExtensionOptions(editor, EXTENSION_TYPE_NAME);
    const Preview = getExternalRenderer(editor, 'inline');
    const deviationMediaService = getExternalRenderer(editor, 'mediaService');
    const fv = deviationMediaService.getLargestFullview(attrs.deviation);
    var _fv_height, _fv_width;
    const ratio = ((_fv_height = fv === null || fv === void 0 ? void 0 : fv.height) !== null && _fv_height !== void 0 ? _fv_height : 0) / ((_fv_width = fv === null || fv === void 0 ? void 0 : fv.width) !== null && _fv_width !== void 0 ? _fv_width : 0);
    let maxWidth = options.maxWidth;
    let maxHeight = options.maxHeight;
    if (deviationMediaService.getDeviationType(attrs.deviation) === 'image') {
        const thumbs = deviationMediaService.getThumbSet(attrs.deviation, {
            thumbTypes: [
                '375W',
                '414W',
                '750W',
                '828W'
            ]
        });
        const largest = thumbs[thumbs.length - 1];
        if (largest) {
            maxWidth = largest.width;
            maxHeight = largest.height;
        }
    }
    const closeSettings = ()=>{
        setShowSettings(false);
    };
    const [showSettings, setShowSettings] = useState(false);
    const { control, errors, trigger, setValue } = useForm();
    var _attrs_width;
    const { field: widthRange } = useController({
        name: 'width',
        control,
        defaultValue: (_attrs_width = attrs.width) !== null && _attrs_width !== void 0 ? _attrs_width : options.defaultWidth
    });
    var _attrs_height;
    const { field: heightRange } = useController({
        name: 'height',
        control,
        defaultValue: (_attrs_height = attrs.height) !== null && _attrs_height !== void 0 ? _attrs_height : options.defaultWidth * ratio
    });
    const { field: aspectToggle } = useController({
        name: 'aspectToggle',
        control,
        defaultValue: `${attrs.aspect}` === 'false' ? false : true
    });
    const { field: croppingSelect } = useController({
        name: 'cropping',
        control,
        defaultValue: 'fill'
    });
    var _attrs_link;
    const { field: link } = useController({
        name: 'link',
        control,
        defaultValue: (_attrs_link = attrs.link) !== null && _attrs_link !== void 0 ? _attrs_link : '',
        rules: {
            validate: (value)=>{
                return value.length === 0 || validateHref(addMissingProtocol(value));
            }
        }
    });
    const onWidthChange = useCallback((e)=>{
        const handleWidthChange = (ex)=>{
            if (ex) {
                editor.chain().daDeviationThumbUpdateAttrs(aspectToggle.value === true ? {
                    width: ex,
                    height: null
                } : {
                    width: ex
                }).run();
            }
        };
        if (aspectToggle.value === true) {
            heightRange.onChange(Math.min(parseInt(e) * ratio, maxHeight !== null && maxHeight !== void 0 ? maxHeight : Infinity));
        }
        widthRange.onChange(e);
        handleWidthChange(e);
    }, [
        aspectToggle.value,
        editor,
        heightRange,
        maxHeight,
        ratio,
        widthRange
    ]);
    const onHeightChange = useCallback((e)=>{
        if (aspectToggle.value === true) {
            return;
        }
        const handleHeightChange = (ex)=>{
            if (ex) {
                var _attrs_width;
                editor.chain().daDeviationThumbUpdateAttrs({
                    height: ex,
                    width: (_attrs_width = attrs.width) !== null && _attrs_width !== void 0 ? _attrs_width : options.defaultWidth
                }).run();
            }
        };
        heightRange.onChange(e);
        handleHeightChange(e);
    }, [
        aspectToggle.value,
        attrs.width,
        editor,
        heightRange,
        options.defaultWidth
    ]);
    const onLinkChange = useCallback((e)=>{
        link.onChange(e);
        trigger('link');
        if (e) {
            const href = e.target.value.trim();
            if (href.length === 0 || validateHref(addMissingProtocol(href))) {
                editor.chain().daDeviationThumbUpdateAttrs({
                    link: href === '' ? null : addMissingProtocol(href)
                }).run();
            }
        }
    }, [
        editor,
        link,
        trigger
    ]);
    const onAspectSelect = useCallback((e)=>{
        trigger('aspectToggle');
        aspectToggle.onChange(e);
        var _attrs_height, _attrs_width;
        editor.chain().daDeviationThumbUpdateAttrs({
            height: (_attrs_height = attrs.height) !== null && _attrs_height !== void 0 ? _attrs_height : heightRange.value,
            width: (_attrs_width = attrs.width) !== null && _attrs_width !== void 0 ? _attrs_width : options.defaultWidth
        }).run();
        editor.chain().daDeviationThumbUpdateAttrs({
            aspect: e
        }).run();
    }, [
        aspectToggle,
        attrs.height,
        attrs.width,
        editor,
        heightRange.value,
        options.defaultWidth,
        trigger
    ]);
    const onCropSelect = useCallback((e)=>{
        trigger('cropping');
        croppingSelect.onChange(e);
        editor.chain().daDeviationThumbUpdateAttrs({
            cropping: e.target.value
        }).run();
    }, [
        croppingSelect,
        editor,
        trigger
    ]);
    const replaceDeviation = useCallback(()=>{
        const onSelectMedia = getStoredCallback(editor, 'onSelectMedia');
        onSelectMedia && onSelectMedia((deviations)=>{
            if (deviations.length > 0) {
                editor.chain().daDeviationThumbReplace(deviations[0]).run();
            }
        }, {});
    }, [
        editor
    ]);
    const currentUser = getCurrentUser(editor);
    const allowLink = (attrs === null || attrs === void 0 ? void 0 : (_attrs_deviation = attrs.deviation) === null || _attrs_deviation === void 0 ? void 0 : (_attrs_deviation_author = _attrs_deviation.author) === null || _attrs_deviation_author === void 0 ? void 0 : _attrs_deviation_author.userId) === (currentUser === null || currentUser === void 0 ? void 0 : currentUser.userId);
    useEffect(()=>{
        var _attrs_width;
        setValue('width', (_attrs_width = attrs.width) !== null && _attrs_width !== void 0 ? _attrs_width : options.defaultWidth);
        var _attrs_height;
        !aspectToggle.value && setValue('height', (_attrs_height = attrs.height) !== null && _attrs_height !== void 0 ? _attrs_height : options.defaultWidth * ratio);
        var _attrs_height1;
        aspectToggle.value && setValue('height', (_attrs_height1 = attrs.height) !== null && _attrs_height1 !== void 0 ? _attrs_height1 : widthRange.value * ratio);
        var _attrs_aspect;
        setValue('aspectToggle', (_attrs_aspect = attrs.aspect) !== null && _attrs_aspect !== void 0 ? _attrs_aspect : 1);
        var _attrs_cropping;
        setValue('cropping', (_attrs_cropping = attrs.cropping) !== null && _attrs_cropping !== void 0 ? _attrs_cropping : 'fill');
        var _attrs_link;
        setValue('link', (_attrs_link = attrs.link) !== null && _attrs_link !== void 0 ? _attrs_link : undefined);
    }, [
        aspectToggle.value,
        attrs.aspect,
        attrs.cropping,
        attrs.height,
        attrs.link,
        attrs.width,
        options.defaultWidth,
        ratio,
        setValue,
        widthRange.value
    ]);
    const devType = deviationMediaService.getDeviationType(attrs.deviation);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, showSettings && /*#__PURE__*/ React.createElement(SidePanel, {
        editor: editor,
        onRequestClose: closeSettings,
        header: /*#__PURE__*/ React.createElement(ModalHeader, {
            heading: t('ext.deviation.settings.title')
        }),
        footer: /*#__PURE__*/ React.createElement(SolidButton, {
            size: "medium",
            variant: "approval-green",
            onClick: closeSettings
        }, t('ext.deviation.settings.btn.done'))
    }, /*#__PURE__*/ React.createElement(Flex, {
        direction: "column",
        gap: 8
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['preview']
    }, /*#__PURE__*/ React.createElement(Preview, {
        deviation: attrs.deviation,
        width: 150,
        height: devType === 'image' ? undefined : 150
    }), /*#__PURE__*/ React.createElement("div", {
        className: s['replace-refresh-btn-wrapper']
    }, /*#__PURE__*/ React.createElement(IconButton, {
        className: cn(s['btn']),
        size: 'small',
        icon: EditIcon,
        tooltipOffset: [
            0,
            8
        ],
        "aria-label": t('ext.deviation.settings.replace'),
        tooltipText: t('ext.deviation.settings.replace'),
        onClick: replaceDeviation
    }))), /*#__PURE__*/ React.createElement("fieldset", {
        className: s['size']
    }, /*#__PURE__*/ React.createElement("legend", null, t('ext.deviation.settings.size.legend')), /*#__PURE__*/ React.createElement("div", {
        className: s['width-range']
    }, /*#__PURE__*/ React.createElement(NumericField, _object_spread_props(_object_spread({
        label: t('ext.gallery.settings.container.width')
    }, widthRange), {
        onChange: onWidthChange,
        maxValue: maxWidth !== null && maxWidth !== void 0 ? maxWidth : options.defaultWidth,
        minValue: 10,
        sliderStep: 1
    })), /*#__PURE__*/ React.createElement(NumericField, _object_spread_props(_object_spread({
        label: 'height'
    }, heightRange), {
        onChange: onHeightChange,
        maxValue: maxHeight,
        minValue: 10,
        sliderStep: 1,
        disabled: aspectToggle.value
    })), /*#__PURE__*/ React.createElement(BooleanInput, _object_spread_props(_object_spread({}, aspectToggle), {
        id: "aspectToggle",
        label: "maintain aspect",
        onChange: onAspectSelect
    })), /*#__PURE__*/ React.createElement(SelectField, _object_spread_props(_object_spread({}, croppingSelect), {
        items: [
            {
                label: 'fill',
                value: 'fill'
            },
            {
                label: 'contain',
                value: 'contain'
            },
            {
                label: 'cover',
                value: 'cover'
            }
        ],
        onChange: onCropSelect,
        label: 'Cropping',
        id: "cropping",
        withBlankOption: false
    })))), options.canLink && allowLink && /*#__PURE__*/ React.createElement(TextInput, _object_spread_props(_object_spread({
        id: "link",
        label: t('ext.deviation.settings.link.label'),
        placeholder: t('ext.deviation.settings.link.placeholder')
    }, link), {
        onChange: onLinkChange,
        validationResult: errors.link ? 'error' : 'success',
        details: errors.link ? 'Invalid Link' : undefined,
        onFocus: (e)=>{
            setTimeout(()=>{
                e.target.scrollIntoView();
            }, 300);
        }
    })))), /*#__PURE__*/ React.createElement(EditorIconButton, {
        featureId: "node:deviation:settings",
        icon: EditorSettings,
        onClick: ()=>{
            setShowSettings(true);
        },
        className: cn(buttonStyles['button']),
        tooltipHoverDelay: 2000,
        tooltipText: t('ext.deviation.toolbar.settings'),
        "aria-label": t('ext.deviation.toolbar.settings')
    }));
};
Settings.displayName = 'Settings';
export default Settings;

//# sourceMappingURL=Settings.js.map